import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { useUser, useCurrentSpace } from "~/hooks/context-hook";
import { UserBillingStatus } from "~/graphql/codegen/graphql";
import { useClientStorage } from "~/utils/upsell-storage-utils";
import {
  checkUserEligibility,
  checkTimingRules,
  checkAndGetActiveTrigger,
  markModalShown,
  markTriggerDismissed,
} from "./upsell-tracking-utils";
import { MindstonePlusUpsellModal } from "./mindstone-plus-upsell-modal";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";

// Modal copy variations
export interface ModalCopy {
  title: MessageDescriptor;
  description: MessageDescriptor;
  cta: MessageDescriptor;
  image?: string;
}

export const copyVariations: ModalCopy[] = [
  {
    title: msg`Save 5+ hours this week`,
    description: msg`Learn how people in roles like yours use AI to draft docs, summarize meetings, and streamline busywork. Get personalised tasks and expert demos in our 4-week program.`,
    cta: msg`Join program to save time`,
  },
  {
    title: msg`Build custom GPTs for your tasks`,
    description: msg`Create GPTs that actually match what you do. Learn how with hands-on personalised exercises, expert walkthroughs, and peer tips, no tech background needed.`,
    cta: msg`Join program to build your GPTs`,
  },
  {
    title: msg`Turn AI skills into proof`,
    description: msg`Complete projects that show what you can actually do, and earn an AI certificate to use in reviews or on LinkedIn.`,
    cta: msg`Join program to get certified`,
  },
  {
    title: msg`Make AI work for your role`,
    description: msg`Follow a clear path with demos, practical tasks, and tools chosen for your role. No fluff. Just skills you can use immediately.`,
    cta: msg`Join an AI program`,
  },
];

// Context type
type MindstonePlusUpsellContextType = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  selectedCopy: ModalCopy | null;
  activeTrigger: string | null;
  handleDismiss: () => void;
  handleCTAClick: () => void;
  recheckTriggers: () => void;
};

const MindstonePlusUpsellContext = createContext<
  MindstonePlusUpsellContextType | undefined
>(undefined);

// Global reference to the recheck function for use by tracking utils
let globalRecheckTriggers: (() => void) | null = null;

export const MindstonePlusUpsellProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const user = useUser();
  const currentSpace = useCurrentSpace();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCopy, setSelectedCopy] = useState<ModalCopy | null>(null);
  const [activeTrigger, setActiveTrigger] = useState<string | null>(null);
  const [triggerCheckCounter, setTriggerCheckCounter] = useState(0);
  const { isClient } = useClientStorage();

  // Function to check triggers and show modal if conditions are met
  const checkTriggersAndShowModal = useCallback(() => {
    if (!isClient) return;

    // Check user eligibility
    const hasActiveSubscription =
      user?.billing?.status === UserBillingStatus.Active;
    const isEnrolledInProgram =
      (currentSpace?.programs?.enrolled?.total ?? 0) > 0;

    if (!checkUserEligibility(hasActiveSubscription, isEnrolledInProgram)) {
      return;
    }

    // Check timing rules
    if (!checkTimingRules(user?.billing?.purchaseDate)) {
      return;
    }

    // Check triggers
    const trigger = checkAndGetActiveTrigger();

    if (trigger) {
      setActiveTrigger(trigger);
      // Randomly select copy variation
      const randomCopy =
        copyVariations[Math.floor(Math.random() * copyVariations.length)];
      setSelectedCopy(randomCopy);
      setIsOpen(true);
      markModalShown();
    }
  }, [
    isClient,
    user?.billing?.status,
    user?.billing?.purchaseDate,
    currentSpace?.programs?.enrolled?.total,
  ]);

  // Function to force re-checking of triggers
  const recheckTriggers = useCallback(() => {
    setTriggerCheckCounter((prev) => prev + 1);
  }, []);

  // Set global reference so tracking utils can call this
  useEffect(() => {
    globalRecheckTriggers = recheckTriggers;
    return () => {
      globalRecheckTriggers = null;
    };
  }, [recheckTriggers]);

  const handleDismiss = () => {
    setIsOpen(false);
    if (activeTrigger) {
      markTriggerDismissed(activeTrigger);
    }
  };

  const handleCTAClick = () => {
    setIsOpen(false);
    // Navigation is handled in the modal component itself
  };

  // Check eligibility and triggers
  useEffect(() => {
    checkTriggersAndShowModal();
  }, [
    checkTriggersAndShowModal,
    triggerCheckCounter, // This will cause re-checking when incremented
  ]);

  return (
    <MindstonePlusUpsellContext.Provider
      value={{
        isOpen,
        setIsOpen,
        selectedCopy,
        activeTrigger,
        handleDismiss,
        handleCTAClick,
        recheckTriggers,
      }}
    >
      {children}
      <MindstonePlusUpsellModal />
    </MindstonePlusUpsellContext.Provider>
  );
};

export const useMindstonePlusUpsell = () => {
  const context = useContext(MindstonePlusUpsellContext);
  if (context === undefined) {
    // Return a safe default context to avoid runtime errors if provider is missing
    // This should rarely happen but provides resilience during navigation edge cases
    return {
      isOpen: false,
      setIsOpen: () => {},
      selectedCopy: null,
      activeTrigger: null,
      handleDismiss: () => {},
      handleCTAClick: () => {},
      recheckTriggers: () => {},
    };
  }
  return context;
};

// Export function for tracking utils to trigger re-check
export const triggerUpsellRecheck = () => {
  if (globalRecheckTriggers) {
    globalRecheckTriggers();
  }
};
